import React from 'react';
import { X, AlertTriangle, BedDouble, Moon, Sun, MapPin, Shield, FileText, Share2, Home, Truck, CheckCircle, BookOpen, Clock, CalendarDays } from 'lucide-react';

// Logout Confirmation Modal
export const LogoutConfirmModal = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-sm w-full border border-slate-700">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">Logout While Working</h3>
      </div>
      <p className="text-slate-300 mb-5 sm:mb-6 text-sm sm:text-base">
        You are logging out while WORKING. This will continue as WORK time.
      </p>
      <div className="flex gap-3">
        <button
          onClick={onConfirm}
          className="flex-1 py-2.5 sm:py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 text-sm sm:text-base"
          data-testid="confirm-logout-btn"
        >
          Confirm
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 sm:py-3 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-500 text-sm sm:text-base"
          data-testid="cancel-logout-btn"
        >
          Cancel & Switch to REST
        </button>
      </div>
    </div>
  </div>
);

// Night Rest Confirmation Modal
export const NightRestConfirmModal = ({ message, confirmType, onProceed, onCancel }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-sm w-full border border-slate-700">
      <div className="flex items-center gap-3 mb-4">
        <BedDouble className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">Night Rest Active</h3>
      </div>
      <p className="text-slate-300 mb-5 sm:mb-6 text-sm sm:text-base">
        {message}
      </p>
      <div className="flex gap-3">
        {confirmType !== 'WORK_BLOCKED' && (
          <button
            onClick={onProceed}
            className="flex-1 py-2.5 sm:py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 text-sm sm:text-base"
            data-testid="night-rest-proceed-btn"
          >
            Proceed
          </button>
        )}
        <button
          onClick={onCancel}
          className={`flex-1 py-2.5 sm:py-3 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-500 text-sm sm:text-base`}
          data-testid="night-rest-cancel-btn"
        >
          {confirmType === 'WORK_BLOCKED' ? 'OK' : 'Cancel'}
        </button>
      </div>
    </div>
  </div>
);

// Journey Planning Modal
export const JourneyPlanningModal = ({ 
  destination, 
  onDestinationChange, 
  onClose, 
  onCheck, 
  loading, 
  suggestions, 
  showSuggestions, 
  onSelectAddress 
}) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-slate-700">
      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
          <h3 className="text-lg sm:text-xl font-bold text-white">Distance Check</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <p className="text-slate-300 mb-4 text-xs sm:text-sm">
        Enter your destination to check if it&apos;s within 100km from your base location.
      </p>
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Enter destination address..."
          value={destination}
          onChange={onDestinationChange}
          autoComplete="off"
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 text-sm sm:text-base"
          data-testid="journey-destination-input"
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-xl z-10 max-h-48 overflow-y-auto">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => onSelectAddress(suggestion)}
                className="w-full px-3 sm:px-4 py-2 text-left text-white hover:bg-slate-600 text-xs sm:text-sm border-b border-slate-600 last:border-0"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 py-2.5 sm:py-3 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-500 text-sm sm:text-base"
        >
          Cancel
        </button>
        <button
          onClick={onCheck}
          disabled={loading || !destination.trim()}
          className="flex-1 py-2.5 sm:py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          data-testid="check-radius-btn"
        >
          {loading ? 'Checking...' : 'Check Radius'}
        </button>
      </div>
    </div>
  </div>
);

// Radius Warning Modal
export const RadiusWarningModal = ({ distanceKm, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-amber-500/50">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">100km Radius Exceeded</h3>
      </div>
      <div className="bg-amber-500/10 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 border border-amber-500/30">
        <p className="text-amber-200 font-medium mb-2 text-sm sm:text-base">
          This destination is more than 100km radius from base.
        </p>
        <p className="text-slate-300 text-xs sm:text-sm">
          The National Driver Work Diary (NDWD) must be completed for the whole day from the start of work today.
        </p>
        {distanceKm && (
          <p className="text-slate-400 text-xs mt-2">
            Distance from base: {distanceKm}km
          </p>
        )}
      </div>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 sm:py-3 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-500 text-sm sm:text-base"
          data-testid="radius-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 py-2.5 sm:py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 text-sm sm:text-base"
          data-testid="radius-confirm-btn"
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
);

// Radius Acknowledged Modal
export const RadiusAcknowledgedModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-emerald-500/50">
      <div className="flex items-center gap-3 mb-4">
        <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">Acknowledged</h3>
      </div>
      <div className="bg-emerald-500/10 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 border border-emerald-500/30">
        <p className="text-emerald-200 font-medium text-sm sm:text-base">
          Please keep using LAWD along with completing the Work Diary.
        </p>
        <p className="text-slate-300 text-xs sm:text-sm mt-2">
          LAWD will continue to track your fatigue management compliance while you complete the required paperwork.
        </p>
      </div>
      <button
        onClick={onClose}
        className="w-full py-2.5 sm:py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 text-sm sm:text-base"
        data-testid="acknowledged-close-btn"
      >
        OK, Got it
      </button>
    </div>
  </div>
);

// Rest Tracking Status Bar
const RestStatusBar = ({ label, icon: Icon, hoursSince, maxHours, requiredBy, overdue }) => {
  const pct = hoursSince != null ? Math.min((hoursSince / maxHours) * 100, 100) : 0;
  const isUrgent = pct >= 80;
  const barColor = overdue ? 'bg-red-500' : isUrgent ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = overdue ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-slate-400';

  const formatHours = (h) => {
    if (h == null) return 'N/A';
    if (h < 1) return `${Math.round(h * 60)}m ago`;
    if (h < 48) return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m ago`;
    return `${Math.floor(h / 24)}d ${Math.floor(h % 24)}h ago`;
  };

  const formatDeadline = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = d - now;
    if (diffMs <= 0) return 'Overdue';
    const diffH = diffMs / 3600000;
    if (diffH < 1) return `in ${Math.round(diffH * 60)}m`;
    if (diffH < 48) return `in ${Math.floor(diffH)}h ${Math.round((diffH % 1) * 60)}m`;
    return `in ${Math.floor(diffH / 24)}d ${Math.floor(diffH % 24)}h`;
  };

  return (
    <div className="bg-slate-700/40 rounded-lg p-3" data-testid={`rest-status-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${overdue ? 'text-red-400' : 'text-slate-300'}`} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
        </div>
        <span className={`text-xs font-mono ${textColor}`}>
          {hoursSince != null ? formatHours(hoursSince) : 'No record'}
        </span>
      </div>
      <div className="w-full bg-slate-600 rounded-full h-1.5 mb-1">
        <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-500">0h</span>
        {requiredBy && (
          <span className={`text-[10px] ${overdue ? 'text-red-400 font-semibold' : 'text-slate-500'}`}>
            Due {formatDeadline(requiredBy)}
          </span>
        )}
        <span className="text-[10px] text-slate-500">{maxHours}h</span>
      </div>
    </div>
  );
};

// Finish Day Modal with Night/Long Rest Decision Screen
export const FinishDayModal = ({ onFinish, onClose, restTracking, regime }) => {
  const isFRHV = regime === 'FRHV';
  const rt = restTracking || {};
  const forceLongRest = rt.force_long_rest;
  const nightOverdue = rt.night_rest_overdue;
  const longOverdue = rt.long_rest_overdue;

  // Determine recommendation
  let recommendation = null;
  let recommendationSeverity = 'info'; // info | warning | critical
  if (forceLongRest || longOverdue) {
    recommendation = 'A 24-hour stationary rest is required. You must take a long rest before your next shift.';
    recommendationSeverity = 'critical';
  } else if (nightOverdue) {
    recommendation = 'Night rest is overdue. You need at least 7 continuous hours rest (with 5h in night window 22:00-08:00).';
    recommendationSeverity = 'critical';
  } else if (rt.hours_since_long_rest != null && rt.hours_since_long_rest >= 144) {
    recommendation = 'Long rest approaching — due within the next 24 hours. Consider taking a 24-hour rest soon.';
    recommendationSeverity = 'warning';
  } else if (rt.hours_since_night_rest != null && rt.hours_since_night_rest >= 20) {
    recommendation = 'Night rest approaching — take a 7-hour night rest before your next shift.';
    recommendationSeverity = 'warning';
  }

  const sevStyles = {
    critical: 'bg-red-500/10 border-red-500/30 text-red-300',
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-slate-700 max-h-[90vh] overflow-y-auto" data-testid="finish-day-modal">
        <div className="flex items-center gap-3 mb-4">
          <Moon className="w-7 h-7 sm:w-8 sm:h-8 text-indigo-400" />
          <h3 className="text-lg sm:text-xl font-bold text-white">Finish Day</h3>
        </div>

        {/* FRHV Rest Tracking Section */}
        {isFRHV && (rt.hours_since_night_rest != null || rt.hours_since_long_rest != null) && (
          <div className="mb-4 space-y-2" data-testid="rest-tracking-section">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">Rest Status (Rolling Window)</p>
            <RestStatusBar
              label="Night Rest"
              icon={Moon}
              hoursSince={rt.hours_since_night_rest}
              maxHours={24}
              requiredBy={rt.night_rest_required_by}
              overdue={nightOverdue}
            />
            <RestStatusBar
              label="Long Rest (24h)"
              icon={CalendarDays}
              hoursSince={rt.hours_since_long_rest}
              maxHours={168}
              requiredBy={rt.long_rest_required_by}
              overdue={longOverdue}
            />
            <div className="flex gap-3 text-[11px] text-slate-500 mt-1 px-1">
              <span>Night rests (14d): <span className="text-slate-300 font-medium">{rt.night_rests_14d ?? '—'}</span></span>
              <span>Long rests (14d): <span className="text-slate-300 font-medium">{rt.long_rests_14d ?? '—'}</span></span>
            </div>
          </div>
        )}

        {/* Recommendation Banner */}
        {recommendation && (
          <div className={`rounded-lg border px-3 py-2.5 mb-4 ${sevStyles[recommendationSeverity]}`} data-testid="rest-recommendation">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs leading-relaxed">{recommendation}</p>
            </div>
          </div>
        )}

        <p className="text-slate-300 mb-4 text-sm sm:text-base">
          Are you driving tomorrow? This helps determine your required rest period.
        </p>

        <div className="space-y-3">
          <button
            onClick={() => onFinish(true)}
            disabled={forceLongRest}
            className={`w-full py-3 sm:py-4 rounded-xl font-semibold flex items-center justify-center gap-2 text-sm sm:text-base transition ${
              forceLongRest
                ? 'bg-slate-600/50 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-500 text-white hover:bg-indigo-600'
            }`}
            data-testid="driving-tomorrow-btn"
          >
            <Sun className="w-5 h-5" />
            Yes, driving tomorrow
            <span className="text-xs ml-1 opacity-70">(7h night rest)</span>
          </button>
          {forceLongRest && (
            <p className="text-xs text-red-400 text-center -mt-1">
              Unavailable — 24h long rest required first
            </p>
          )}
          <button
            onClick={() => onFinish(false)}
            className="w-full py-3 sm:py-4 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-500 flex items-center justify-center gap-2 text-sm sm:text-base"
            data-testid="not-driving-tomorrow-btn"
          >
            <Moon className="w-5 h-5" />
            No, not driving tomorrow
            <span className="text-xs ml-1 opacity-70">(Day off / long rest)</span>
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 sm:py-3 text-slate-400 hover:text-white text-sm sm:text-base"
            data-testid="finish-day-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Service Modal (was About)
export const ServiceModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-lg w-full border border-slate-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
          <Truck className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
        </div>
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-white">LAWD</h3>
          <p className="text-slate-400 text-xs sm:text-sm">Local Australian Work Diary</p>
        </div>
      </div>

      <div className="space-y-4 text-slate-300 mb-5">
        {/* Service Description */}
        <div className="bg-slate-700/30 rounded-xl p-4">
          <h4 className="text-white font-semibold text-sm mb-2">About This Service</h4>
          <p className="text-xs sm:text-sm leading-relaxed">
            LAWD is an electronic record-keeping service that assists operators and record keepers
            in fulfilling their record-keeping obligations under the Heavy Vehicle National Law.
          </p>
        </div>

        {/* Electronic Records Statement */}
        <div className="bg-slate-700/30 rounded-xl p-4">
          <h4 className="text-white font-semibold text-sm mb-2">Electronic Records</h4>
          <p className="text-xs sm:text-sm leading-relaxed">
            Where legislation refers to written or paper records, this service provides
            the equivalent in electronic form. Records maintained within this service are
            created, stored, and retrievable electronically. They are date- and time-stamped,
            attributable to the relevant individual, and protected against unauthorised
            alteration. All changes are recorded in an audit trail.
          </p>
        </div>

        {/* Scope of Service */}
        <div className="bg-slate-700/30 rounded-xl p-4">
          <h4 className="text-white font-semibold text-sm mb-2">Scope of Service</h4>
          <p className="text-xs sm:text-sm text-slate-400 mb-3">
            This service maintains records of:
          </p>
          <ul className="space-y-1.5 text-xs sm:text-sm text-slate-300">
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-0.5">&#10003;</span>
              Driver identity, contact details, and licence information
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-0.5">&#10003;</span>
              Dates and times of driving fatigue-regulated heavy vehicles
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-0.5">&#10003;</span>
              Vehicle and trailer registration numbers
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-0.5">&#10003;</span>
              Total work and rest times per day and per week
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-0.5">&#10003;</span>
              Driver base location and history of changes
            </li>
          </ul>
        </div>

        {/* Business Operations Note */}
        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
          <h4 className="text-amber-400 font-semibold text-sm mb-2">Operator Responsibilities</h4>
          <p className="text-xs sm:text-sm leading-relaxed text-slate-300">
            The following are business operations performed outside of this service
            and remain the responsibility of the operator:
          </p>
          <ul className="mt-2 space-y-1.5 text-xs sm:text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-0.5">&bull;</span>
              Scheduling, rostering, and driver allocation decisions
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-0.5">&bull;</span>
              Driver changeover arrangements
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-0.5">&bull;</span>
              Payroll, timesheets, and payment records
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-0.5">&bull;</span>
              Vehicle maintenance and roadworthiness decisions
            </li>
          </ul>
        </div>

        <p className="text-xs text-slate-500">
          Version 6.2.0 &middot; For support: support@lawd.com.au
        </p>
      </div>

      <button
        onClick={onClose}
        className="w-full py-2.5 sm:py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 text-sm sm:text-base"
        data-testid="service-close-btn"
      >
        Close
      </button>
    </div>
  </div>
);

// Export PDF Modal
export const ExportPDFModal = ({ onExport, onShare, loading, onClose }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-slate-700">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-7 h-7 sm:w-8 sm:h-8 text-blue-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">Export Work Diary</h3>
      </div>
      <p className="text-slate-300 mb-5 sm:mb-6 text-xs sm:text-sm">
        Export your 14-day work diary as PDF. Choose how you want to share it:
      </p>
      <div className="space-y-3">
        <button
          onClick={onExport}
          disabled={loading}
          className="w-full py-3 sm:py-4 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
          data-testid="export-view-btn"
        >
          <FileText className="w-5 h-5" />
          {loading ? 'Generating...' : 'Download PDF'}
        </button>
        <button
          onClick={onShare}
          disabled={loading}
          className="w-full py-3 sm:py-4 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
          data-testid="export-sms-btn"
        >
          <Share2 className="w-5 h-5" />
          {loading ? 'Creating link...' : 'Share via SMS'}
          <span className="text-xs ml-2 opacity-70">(1hr link)</span>
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 sm:py-3 text-slate-400 hover:text-white text-sm sm:text-base"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// Base Address Modal
export const BaseAddressModal = ({ value, onChange, timezone, onSave, onClose }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-slate-700">
      <div className="flex items-center gap-3 mb-4">
        <Home className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">Base Address</h3>
      </div>
      <p className="text-slate-300 mb-4 text-xs sm:text-sm">
        Enter your base depot address. This is used for 100km radius calculations and timezone.
      </p>
      <div className="mb-4">
        <label className="block text-xs sm:text-sm text-slate-400 mb-2">Address</label>
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder="e.g., 10 Railway Terrace, North Sydney"
          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm sm:text-base"
          data-testid="base-address-input"
        />
      </div>
      {timezone && (
        <p className="text-xs text-slate-500 mb-4">
          Current timezone: {timezone}
        </p>
      )}
      <div className="space-y-3">
        <button
          onClick={onSave}
          disabled={!value.trim()}
          className="w-full py-2.5 sm:py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50 text-sm sm:text-base"
          data-testid="base-address-save-btn"
        >
          Save Base Address
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 sm:py-3 text-slate-400 hover:text-white text-sm sm:text-base"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// Driver Acknowledgement Modal
export const AcknowledgementModal = ({ checked, onCheckChange, onSubmit }) => (
  <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4">
    <div className="bg-slate-800 rounded-2xl max-w-lg w-full border border-slate-700 max-h-[90vh] overflow-y-auto">
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">Driver Acknowledgement</h3>
            <p className="text-slate-400 text-xs sm:text-sm">Fatigue Responsibility</p>
          </div>
        </div>
        
        <div className="text-xs sm:text-sm text-slate-300 space-y-3 sm:space-y-4 mb-5 sm:mb-6">
          <p className="font-medium text-white">I acknowledge and agree that:</p>
          
          <div className="space-y-2.5 sm:space-y-3 text-slate-400">
            <p><span className="text-white font-medium">1.</span> LAWD is a fatigue management advisory system and compliance system for recording work and rest periods for vehicles 12t or more, operating within 100km radius from your base.</p>
            
            <p><span className="text-white font-medium">2.</span> Compliance with all applicable heavy vehicle laws remains your responsibility, warnings do not remove your legal responsibility, including not to drive whilst tired.</p>
            
            <p><span className="text-white font-medium">3.</span> The LAW layer of times reflects fatigue regulated rules and relies on you using the service to nominate the correct vehicle and accurately record work and rest activities.</p>
            
            <p><span className="text-white font-medium">4.</span> The other layers are business safety enhancements and are business requirements.</p>
            
            <p><span className="text-white font-medium">5.</span> Historical or imported data may not reflect complete compliance history.</p>
            
            <p><span className="text-white font-medium">6.</span> In using this service, you will be operating within applicable regulated radius limits, however you must always have your paper National Driver Work Diary (NDWD) with you and when outside regulated radius limits, I will comply with NDWD requirements in addition to this system.</p>
          </div>
          
          <p className="text-amber-400 font-medium mt-4">
            I confirm that I understand that LAWD does not replace my legal obligations.
          </p>
        </div>
        
        <label className="flex items-start gap-3 mb-5 sm:mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheckChange}
            className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
            data-testid="acknowledgement-checkbox"
          />
          <span className="text-white font-medium text-sm sm:text-base">I Agree</span>
        </label>
        
        <button
          onClick={onSubmit}
          disabled={!checked}
          className="w-full py-3 sm:py-4 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          data-testid="acknowledgement-submit-btn"
        >
          Continue to LAWD
        </button>
      </div>
    </div>
  </div>
);

// Vehicle Selection Modal
export const VehicleSelectionModal = ({ vehicles, selectedVehicleId, loading, onSelect, onAddOther, onClose }) => {
  const [pickedVehicle, setPickedVehicle] = React.useState(null);
  const [trailers, setTrailers] = React.useState([]);
  const [selectedTrailers, setSelectedTrailers] = React.useState([]);
  const [trailersLoaded, setTrailersLoaded] = React.useState(false);

  // Load trailers when a vehicle is picked
  React.useEffect(() => {
    if (pickedVehicle && !trailersLoaded) {
      const { api } = require('../../utils/api');
      api('/trailers').then(res => {
        setTrailers(res.trailers || []);
        setTrailersLoaded(true);
      }).catch(() => setTrailersLoaded(true));
    }
  }, [pickedVehicle, trailersLoaded]);

  const toggleTrailer = (trailerId) => {
    setSelectedTrailers(prev =>
      prev.includes(trailerId) ? prev.filter(id => id !== trailerId) : [...prev, trailerId]
    );
  };

  const handleConfirm = () => {
    if (pickedVehicle) {
      onSelect(pickedVehicle.vehicle_id, pickedVehicle, selectedTrailers);
    }
  };

  return (
  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-slate-800 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <h2 className="text-white font-semibold text-sm sm:text-base">Select Vehicle</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4 overflow-y-auto max-h-[60vh]">
        {vehicles.length === 0 ? (
          <p className="text-slate-400 text-center py-6 sm:py-8 text-sm">No vehicles available</p>
        ) : (
          <div className="space-y-2">
            {vehicles.map(v => (
              <button
                key={v.vehicle_id}
                className={`w-full p-2.5 sm:p-3 rounded-xl border text-left transition ${
                  (pickedVehicle?.vehicle_id || selectedVehicleId) === v.vehicle_id
                    ? 'bg-emerald-500/20 border-emerald-500/50'
                    : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                }`}
                onClick={() => setPickedVehicle(v)}
                disabled={loading}
                data-testid={`vehicle-option-${v.vehicle_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium text-sm sm:text-base">{v.rego}</span>
                    {v.provisional && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                        Provisional
                      </span>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    v.regime === 'FRHV' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {v.regime_label}
                  </span>
                </div>
                <div className="text-xs sm:text-sm text-slate-400 mt-1">
                  {v.vehicle_class} | {v.gvm_kg?.toLocaleString()} kg
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Trailer selection — only shown when a vehicle is picked */}
        {pickedVehicle && trailersLoaded && trailers.length > 0 && (
          <div className="mt-4 border-t border-slate-700 pt-3">
            <p className="text-slate-400 text-xs mb-2">Attach Trailer(s) — optional</p>
            <div className="space-y-1.5">
              {trailers.map(t => (
                <button
                  key={t.trailer_id}
                  className={`w-full p-2 rounded-lg border text-left text-sm transition flex items-center gap-2 ${
                    selectedTrailers.includes(t.trailer_id)
                      ? 'bg-blue-500/20 border-blue-500/50 text-white'
                      : 'bg-slate-700/30 border-slate-600 text-slate-300 hover:border-slate-500'
                  }`}
                  onClick={() => toggleTrailer(t.trailer_id)}
                  data-testid={`trailer-option-${t.trailer_id}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    selectedTrailers.includes(t.trailer_id)
                      ? 'bg-blue-500 border-blue-500' : 'border-slate-500'
                  }`}>
                    {selectedTrailers.includes(t.trailer_id) && (
                      <CheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="font-medium">{t.registration}</span>
                  <span className="text-slate-500 text-xs ml-auto">{t.trailer_type}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        <button
          className="w-full mt-4 p-2.5 sm:p-3 rounded-xl border border-dashed border-slate-500 text-slate-300 hover:border-emerald-500 hover:text-emerald-400 transition text-sm sm:text-base"
          onClick={onAddOther}
          data-testid="add-other-vehicle-btn"
        >
          + Add Other Vehicle
        </button>

        {/* Confirm button when vehicle is picked */}
        {pickedVehicle && (
          <button
            className="w-full mt-3 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 text-sm sm:text-base"
            onClick={handleConfirm}
            disabled={loading}
            data-testid="confirm-vehicle-btn"
          >
            {loading ? 'Selecting...' : `Confirm${selectedTrailers.length > 0 ? ` + ${selectedTrailers.length} trailer${selectedTrailers.length > 1 ? 's' : ''}` : ''}`}
          </button>
        )}
      </div>
    </div>
  </div>
  );
};

// Other Vehicle Form Modal
export const OtherVehicleFormModal = ({ form, onChange, loading, onSubmit, onClose }) => (
  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-slate-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <h2 className="text-white font-semibold text-sm sm:text-base">Add Other Vehicle</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={onSubmit} className="p-4 space-y-3 sm:space-y-4">
        <div>
          <label className="text-xs sm:text-sm text-slate-400 block mb-1">Registration (Rego)</label>
          <input
            type="text"
            value={form.rego}
            onChange={e => onChange({ ...form, rego: e.target.value.toUpperCase() })}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 sm:py-2.5 text-white text-sm sm:text-base"
            placeholder="ABC-123"
            required
            data-testid="other-vehicle-rego"
          />
        </div>
        <div>
          <label className="text-xs sm:text-sm text-slate-400 block mb-1">Vehicle Class</label>
          <select
            value={form.vehicle_class}
            onChange={e => onChange({ ...form, vehicle_class: e.target.value })}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 sm:py-2.5 text-white text-sm sm:text-base"
            data-testid="other-vehicle-class"
          >
            <option value="FRHV">FRHV (Heavy - 12t+)</option>
            <option value="HV">HV (Heavy Vehicle)</option>
            <option value="LV">LV (Light Vehicle)</option>
            <option value="CAR_CARAVAN">Car/Caravan</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs sm:text-sm text-slate-400 block mb-1">GVM/GCM (kg)</label>
          <input
            type="number"
            value={form.gvm_kg}
            onChange={e => onChange({ ...form, gvm_kg: e.target.value })}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 sm:py-2.5 text-white text-sm sm:text-base"
            placeholder="e.g., 8500"
            min="0"
            max="100000"
            required
            data-testid="other-vehicle-gvm"
          />
          <p className="text-xs text-slate-500 mt-1">
            12,000 kg or more = FRHV compliance | Below 12,000 kg = Business rules
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 sm:py-2.5 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2 sm:py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 text-sm sm:text-base"
            data-testid="submit-other-vehicle"
          >
            {loading ? 'Adding...' : 'Add Vehicle'}
          </button>
        </div>
      </form>
    </div>
  </div>
);


// Primary Production Radius Selection Modal
export const PrimaryProductionRadiusModal = ({ 
  vehicle, 
  onSelectRadius, 
  onCancel, 
  loading 
}) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-md w-full border border-emerald-500/50">
      <div className="flex items-center gap-3 mb-4">
        <Truck className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400" />
        <h3 className="text-lg sm:text-xl font-bold text-white">Select Journey Radius</h3>
      </div>
      
      <div className="bg-emerald-500/10 rounded-xl p-3 sm:p-4 mb-5 border border-emerald-500/30">
        <p className="text-emerald-200 font-medium mb-2 text-sm sm:text-base">
          Primary Production Vehicle Detected
        </p>
        <p className="text-slate-300 text-xs sm:text-sm">
          Vehicle: {vehicle?.rego || 'Unknown'}
        </p>
        <p className="text-slate-400 text-xs mt-2">
          This vehicle is eligible for the 160km Primary Production radius. 
          Please select which radius applies to your journey.
        </p>
      </div>

      <div className="space-y-3 mb-5">
        <button
          onClick={() => onSelectRadius(100)}
          disabled={loading}
          className="w-full p-4 bg-slate-700/50 border border-slate-600 rounded-xl text-left hover:border-blue-500 hover:bg-slate-700 transition-all disabled:opacity-50"
          data-testid="select-100km-radius"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-sm sm:text-base">100km Standard Radius</p>
              <p className="text-slate-400 text-xs mt-1">Standard work radius for all journeys</p>
            </div>
            <div className="text-blue-400 font-bold text-lg">100km</div>
          </div>
        </button>

        <button
          onClick={() => onSelectRadius(160)}
          disabled={loading}
          className="w-full p-4 bg-emerald-500/10 border border-emerald-500/50 rounded-xl text-left hover:border-emerald-400 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
          data-testid="select-160km-radius"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-300 font-semibold text-sm sm:text-base">160km Primary Production Radius</p>
              <p className="text-slate-400 text-xs mt-1">Extended radius for eligible primary production work</p>
            </div>
            <div className="text-emerald-400 font-bold text-lg">160km</div>
          </div>
        </button>
      </div>

      <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
        <p className="text-slate-400 text-xs">
          <strong className="text-slate-300">Note:</strong> Your selection will be logged for compliance purposes. 
          The 160km radius is only valid for journeys that fall under the Primary Production exemption.
        </p>
      </div>

      <button
        onClick={onCancel}
        disabled={loading}
        className="w-full py-2.5 sm:py-3 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-500 text-sm sm:text-base disabled:opacity-50"
        data-testid="cancel-radius-selection"
      >
        Cancel
      </button>
    </div>
  </div>
);

